import { useCallback, useEffect, useState } from "react";

import BugReportIcon from "@mui/icons-material/BugReport";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";

import { apiBaseUrls } from "@platform/constants/shared-constants";

const authHeaders = () => ({
  withCredentials: true as const,
});

export function IntegrationsSection() {
  // Integration state
  const [jiraStatus, setJiraStatus] = useState<{
    connected: boolean;
    siteUrl?: string;
  } | null>(null);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [linearStatus, setLinearStatus] = useState<{
    connected: boolean;
    workspaceSlug?: string;
  } | null>(null);
  const [linearLoading, setLinearLoading] = useState(false);
  const [mondayStatus, setMondayStatus] = useState<{
    connected: boolean;
    accountSlug?: string;
  } | null>(null);
  const [mondayLoading, setMondayLoading] = useState(false);

  const loadIntegrationStatuses = useCallback(async () => {
    try {
      const [jiraRes, linearRes, mondayRes] = await Promise.all([
        axios
          .get(`${apiBaseUrls.BACKEND}/api/v1/integrations/jira/status`, {
            ...authHeaders(),
          })
          .catch(() => ({ data: { connected: false } })),
        axios
          .get(`${apiBaseUrls.BACKEND}/api/v1/integrations/linear/status`, {
            ...authHeaders(),
          })
          .catch(() => ({ data: { connected: false } })),
        axios
          .get(`${apiBaseUrls.BACKEND}/api/v1/integrations/monday/status`, {
            ...authHeaders(),
          })
          .catch(() => ({ data: { connected: false } })),
      ]);
      setJiraStatus(jiraRes.data as typeof jiraStatus);
      setLinearStatus(linearRes.data as typeof linearStatus);
      setMondayStatus(mondayRes.data as typeof mondayStatus);
    } catch {
      // Integrations are optional — silently fail
    }
  }, []);

  const handleJiraConnect = useCallback(async () => {
    setJiraLoading(true);
    try {
      const res = await axios.get<{ url: string }>(
        `${apiBaseUrls.BACKEND}/api/v1/integrations/jira/authorize`,
        { ...authHeaders() },
      );
      window.open(res.data.url, "jira-oauth", "width=900,height=700");
    } catch {
      // Handle error
    } finally {
      setJiraLoading(false);
    }
  }, []);

  const handleJiraDisconnect = useCallback(async () => {
    setJiraLoading(true);
    try {
      await axios.delete(`${apiBaseUrls.BACKEND}/api/v1/integrations/jira`, {
        headers: authHeaders(),
      });
      setJiraStatus({ connected: false });
    } catch {
      // Handle error
    } finally {
      setJiraLoading(false);
    }
  }, []);

  const handleLinearConnect = useCallback(async () => {
    setLinearLoading(true);
    try {
      const res = await axios.get<{ url: string }>(
        `${apiBaseUrls.BACKEND}/api/v1/integrations/linear/authorize`,
        { ...authHeaders() },
      );
      window.open(res.data.url, "linear-oauth", "width=900,height=700");
    } catch {
      // Handle error
    } finally {
      setLinearLoading(false);
    }
  }, []);

  const handleLinearDisconnect = useCallback(async () => {
    setLinearLoading(true);
    try {
      await axios.delete(`${apiBaseUrls.BACKEND}/api/v1/integrations/linear`, {
        headers: authHeaders(),
      });
      setLinearStatus({ connected: false });
    } catch {
      // Handle error
    } finally {
      setLinearLoading(false);
    }
  }, []);

  const handleMondayConnect = useCallback(async () => {
    setMondayLoading(true);
    try {
      const res = await axios.get<{ url: string }>(
        `${apiBaseUrls.BACKEND}/api/v1/integrations/monday/authorize`,
        { ...authHeaders() },
      );
      window.open(res.data.url, "monday-oauth", "width=900,height=700");
    } catch {
      // Handle error
    } finally {
      setMondayLoading(false);
    }
  }, []);

  const handleMondayDisconnect = useCallback(async () => {
    setMondayLoading(true);
    try {
      await axios.delete(`${apiBaseUrls.BACKEND}/api/v1/integrations/monday`, {
        headers: authHeaders(),
      });
      setMondayStatus({ connected: false });
    } catch {
      // Handle error
    } finally {
      setMondayLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadIntegrationStatuses();
  }, [loadIntegrationStatuses]);

  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" gutterBottom>
        Integrations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect project management tools to sync issues and track work alongside
        your deployments.
      </Typography>

      <Stack spacing={2}>
        {/* Jira card */}
        <Card variant="outlined">
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <BugReportIcon color="action" sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600}>Jira</Typography>
              <Typography variant="body2" color="text.secondary">
                {jiraStatus?.connected
                  ? (jiraStatus.siteUrl ?? "Connected")
                  : "Not connected"}
              </Typography>
            </Box>
            <Chip
              label={jiraStatus?.connected ? "Connected" : "Disconnected"}
              size="small"
              color={jiraStatus?.connected ? "success" : "default"}
            />
            {jiraStatus?.connected ? (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => void handleJiraDisconnect()}
                disabled={jiraLoading}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => void handleJiraConnect()}
                disabled={jiraLoading}
                startIcon={
                  jiraLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Connect
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Linear card */}
        <Card variant="outlined">
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LinearScaleIcon color="action" sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600}>Linear</Typography>
              <Typography variant="body2" color="text.secondary">
                {linearStatus?.connected
                  ? (linearStatus.workspaceSlug ?? "Connected")
                  : "Not connected"}
              </Typography>
            </Box>
            <Chip
              label={linearStatus?.connected ? "Connected" : "Disconnected"}
              size="small"
              color={linearStatus?.connected ? "success" : "default"}
            />
            {linearStatus?.connected ? (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => void handleLinearDisconnect()}
                disabled={linearLoading}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => void handleLinearConnect()}
                disabled={linearLoading}
                startIcon={
                  linearLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Connect
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Monday.com card */}
        <Card variant="outlined">
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ViewWeekIcon color="action" sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600}>Monday.com</Typography>
              <Typography variant="body2" color="text.secondary">
                {mondayStatus?.connected
                  ? (mondayStatus.accountSlug ?? "Connected")
                  : "Not connected"}
              </Typography>
            </Box>
            <Chip
              label={mondayStatus?.connected ? "Connected" : "Disconnected"}
              size="small"
              color={mondayStatus?.connected ? "success" : "default"}
            />
            {mondayStatus?.connected ? (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => void handleMondayDisconnect()}
                disabled={mondayLoading}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => void handleMondayConnect()}
                disabled={mondayLoading}
                startIcon={
                  mondayLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Connect
              </Button>
            )}
          </CardContent>
        </Card>
      </Stack>
    </>
  );
}
